import React from 'react'
import { Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { GenericFormButtonType } from 'components/forms/GenericForm';

/**
 * 
 * GenericDialog inform users about a task and can contain critical information, require decisions, or involve multiple tasks.
 * 
 * A GenericDialog is a type of modal window that appears in front of app content to provide critical information or ask for a decision. Dialogs disable all app functionality when they appear, and remain on screen until confirmed, dismissed, or a required action has been taken.
 * GenericDialogs are purposefully interruptive, so they should be used sparingly.
 * 
 * ###Import to use it
 *
 *```jsx
 *   import { GenericDialog } from '@eco/react-lib'
 *```
 *
 *### Usage
 *
 *```jsx
 *<GenericDialog
 *    dialog={{
 *      btnCancel: {
 *        label: 'String'
 *      },
 *      btnOk: {
 *        label: 'String'
 *      },
 *      content: 'string' | React.ReactNode,,
 *      hideDismissButton: boolean,
 *      img: 'string',
 *      text: 'String',
 *      title: 'String'
 *    }}
 *   open = boolean
 *  />
 *```
 *
 *### Basic Example
 */

export const GenericDialog = ({ open, dialog, onClose, onSubmit, fullWidth, ...props }: GenericDialogType) => {

    const { title, content, text, img, btnCancel, btnOk, hideDismissButton } = dialog
    const handleClose = () => onClose?.()
    const handleSubmit = () => onSubmit?.()

    return (
        <Dialog
            onClose={handleClose}
            aria-labelledby="form-dialog-title"
            open={open}
            fullWidth={fullWidth}
            {...props}
            >
            
            <DialogTitle id="form-dialog-title">
                { title }
                {
                    hideDismissButton !== true &&
                    <IconButton color="default" component="span" className="float-right" onClick={handleClose}>
                        <Close  />
                    </IconButton>
                }
            </DialogTitle>
            
            <DialogContent>
                

                <div className="text-center">
                    {
                        text && <p className="text-bold">{ text.split('\n').map((item, index) => <React.Fragment key={index}>{item} <br/></React.Fragment>) }</p>
                    }
                    

                    <img src={img} />
                    { content }
                </div>
            </DialogContent>

            {
                (btnCancel || btnOk) &&
                <DialogActions>
                    {
                        btnCancel && 
                        <Button 
                            className={`btn-default ${btnCancel?.className}`} 
                            variant="contained"
                            type="button"
                            startIcon={btnCancel?.startIcon}
                            endIcon={btnCancel?.endIcon}
                            onClick={handleClose}>
                            {btnCancel?.label}
                        </Button>
                    }
                    {
                        btnOk && 
                        <Button 
                            className={`ml-auto ${btnOk?.className}`} 
                            variant="contained"
                            type="submit"
                            startIcon={btnOk?.startIcon}
                            endIcon={btnOk?.endIcon}
                            onClick={handleSubmit}>
                            {btnOk?.label}
                        </Button>
                    }
                </DialogActions>
            }
        </Dialog>
    );
}

export type DialogType = {
    /** Title of the dialog */
    title?: string,
    /** Raw content. React component can be used inside dialog */
    content?: string | React.ReactNode,
    /** Text to show inside dialog */
    text?: string,
    /** Image to show inside dialog */
    img?: string,
    btnOk?: GenericFormButtonType,
    btnCancel?: GenericFormButtonType,
    hideDismissButton?: boolean
}

export type GenericDialogType = DialogProps & {
    dialog: DialogType,
    open: DialogProps['open'],
    /** Callback when dialog is about to close */
    onClose?: () => void
    /** Callback when dialog is about to submit */
    onSubmit?: () => void
    fullWidth?: boolean 
}
